/**
 * OpenAI Chat Completions Provider — Streaming chat completions
 *
 * Slimmed-down provider that uses extracted modules for:
 * - SSE parsing (sseParser.ts)
 * - Tool definitions & execution (tools.ts)
 * - Model pricing & capabilities (models.ts)
 */
import type { Workspace } from '../../types'
import type {
    AgentConfig,
    AgentConversationHistory,
    AgentRunController,
    TranscriptEvent,
    ResolvedRef,
    Attachment,
    HistoryContentPart,
    ResponseConversationFunctionCallItem,
    ResponseConversationItem,
    ResponseConversationMessageItem,
    ResponseConversationReasoningItem,
} from '../../agentTypes'
import { buildResponsesProxyUrl, getProviderApiMode } from '../../agentTypes'
import { recordBrowserHttpErrorResponse } from '@secureblob/sdk/browserHttpErrors'
import { parseSSEStream } from './sseParser'
import { createAgentToolRegistry } from './agentToolRegistry'
import { getTokenPrice } from './models'
import { buildChatUserContent, buildResponsesUserMessageItem } from '../../agent/conversationHistory'

/**
 * Optional runtime dependencies for the chat-completions run controller.
 *
 * Enables the provider to use a caller-supplied `fetch` instead of the
 * browser global, so the same controller works in both the webapp and
 * guest-side component-job runtimes.
 */
export interface OpenAiChatCompletionsRunControllerDependencies {
    /**
     * Runtime `fetch` implementation. When supplied, this is used for:
     * - The chat-completions / responses provider POST
     * - MCP remote-tool calls via `createAgentToolRegistry`
     *
     * Defaults to the browser global `fetch` when omitted.
     */
    fetchImpl?: typeof fetch
}

// ────────────────────────────────────────────────────────────────
// Types
// ────────────────────────────────────────────────────────────────

/** Content part for multipart messages — covers common chat-completions modalities */
export type ContentPart = HistoryContentPart

/**
 * Provider plugins — extend model capabilities per-request when supported.
 * See: https://openrouter.ai/docs/features/plugins
 */
export interface OpenAiChatCompletionsPlugin {
    id: 'web' | 'pdf' | 'response-healing' | (string & {})
    enabled?: boolean          // false to disable a default-on plugin
    max_results?: number       // web plugin: limit search results
    [key: string]: unknown     // future plugin params
}

interface ToolCallChunk {
    id?: string
    index?: number
    function?: { name?: string; arguments?: string }
}

interface ChatMessage {
    role: 'system' | 'user' | 'assistant' | 'tool'
    content: string | ContentPart[] | null
    tool_calls?: ToolCallChunk[]
    tool_call_id?: string
    name?: string
}

interface ResponsesFunctionTool {
    type: 'function'
    name: string
    description: string
    parameters: Record<string, unknown>
}

interface ResponsesUsage {
    input_tokens?: number
    output_tokens?: number
}

interface ResponsesOutputMessage {
    type: 'message'
    id?: string
    role?: 'assistant' | 'user'
    status?: string
    phase?: 'commentary' | 'final_answer'
    content?: Array<{ type?: string; text?: string; annotations?: unknown[] }>
}

function isSecureBlobResponsesProxyUrl(url: string): boolean {
    return url.startsWith('/api/responses-proxy')
}

interface ResponsesReasoningOutput {
    type: 'reasoning'
    id: string
    summary?: Array<{ type?: string; text?: string } | string>
    encrypted_content?: string
    status?: string
}

interface ResponsesStreamEvent {
    type?: string
    delta?: string
    arguments?: string
    item?: Record<string, unknown>
    item_id?: string
    output_index?: number
    response?: {
        output?: Array<Record<string, unknown>>
        usage?: ResponsesUsage
    }
}

// ────────────────────────────────────────────────────────────────
// Helpers
// ────────────────────────────────────────────────────────────────

function getSpendLimit(config: AgentConfig): number | null {
    const raw = config.limits?.maxSpendUsd ?? config.limits?.softSpendUsd
    if (!raw) return null
    const parsed = parseFloat(raw)
    if (!Number.isFinite(parsed) || parsed <= 0) return null
    return parsed
}

function generateId(): string {
    return Math.random().toString(36).slice(2, 10)
}

function formatLocalIsoDate(date: Date): string {
    const year = date.getFullYear()
    const month = String(date.getMonth() + 1).padStart(2, '0')
    const day = String(date.getDate()).padStart(2, '0')
    return `${year}-${month}-${day}`
}

export function renderSystemPromptTemplate(systemPrompt: string | undefined, now = new Date()): string | undefined {
    if (!systemPrompt) return systemPrompt
    return systemPrompt.replace(/\{\{\s*date\s*\}\}/g, formatLocalIsoDate(now))
}

function getProviderHostname(baseUrl: string | undefined): string {
    if (!baseUrl) return ''
    try {
        return new URL(baseUrl).hostname.toLowerCase()
    } catch {
        return ''
    }
}

function hasImageInputs(refs: ResolvedRef[], attachments?: Attachment[]): boolean {
    return refs.some((ref) => ref.isImage) || (attachments?.some((attachment) => attachment.mime.startsWith('image/')) ?? false)
}

function hasImageInChatContent(content: ChatMessage['content']): boolean {
    if (!Array.isArray(content)) return false
    return content.some((part) => part?.type === 'image_url')
}

function hasImagesInChatMessages(messages: ChatMessage[]): boolean {
    return messages.some((message) => message.role === 'user' && hasImageInChatContent(message.content))
}

function hasImagesInResponsesInput(input: ResponseConversationItem[]): boolean {
    return input.some((item) => item.type === 'message' && item.content.some((part) => part.type === 'input_image'))
}

function formatProviderTransportError(err: unknown, includesImages: boolean): string {
    const fallback = err instanceof Error ? err.message : String(err)
    const normalized = fallback.trim().toLowerCase()
    const isBrowserLoadFailure = normalized === 'load failed'
        || normalized.includes('failed to fetch')
        || normalized.includes('networkerror')
        || normalized.includes('network request failed')

    if (isBrowserLoadFailure && includesImages) {
        return 'Provider error: network request failed while uploading image input. Try a smaller image (or JPEG), then retry.'
    }

    return `Provider error: ${fallback}`
}

export function buildReasoningPayload(
    baseUrl: string | undefined,
    reasoningEffort: AgentConfig['provider']['reasoningEffort'],
): Record<string, unknown> {
    if (!reasoningEffort) return {}
    const hostname = getProviderHostname(baseUrl)
    if (hostname.includes('openrouter.ai')) {
        return {
            reasoning: {
                effort: reasoningEffort,
            },
        }
    }
    return { reasoning_effort: reasoningEffort }
}

export function buildResponsesReasoningPayload(
    reasoningEffort: AgentConfig['provider']['reasoningEffort'],
): Record<string, unknown> {
    if (!reasoningEffort) return {}
    return {
        reasoning: {
            effort: reasoningEffort,
        },
    }
}

function collectReasoningText(value: unknown, depth = 0, preserveWhitespace = false): string[] {
    if (depth > 4 || value == null) return []
    if (typeof value === 'string') {
        if (preserveWhitespace) {
            return value.length > 0 ? [value] : []
        }
        const trimmed = value.trim()
        return trimmed ? [trimmed] : []
    }
    if (Array.isArray(value)) {
        return value.flatMap((entry) => collectReasoningText(entry, depth + 1, preserveWhitespace))
    }
    if (typeof value === 'object') {
        const record = value as Record<string, unknown>
        const segments: string[] = []
        for (const key of ['text', 'reasoning', 'content', 'summary', 'reasoning_content', 'reasoning_text', 'thinking', 'thinking_content']) {
            segments.push(...collectReasoningText(record[key], depth + 1, preserveWhitespace))
        }
        return segments
    }
    return []
}

export function extractThinkingDeltaText(delta: Record<string, unknown> | undefined): string | undefined {
    if (!delta) return undefined
    const streamedSegments = [
        ...collectReasoningText(delta.reasoning_content, 0, true),
        ...collectReasoningText(delta.reasoning_text, 0, true),
        ...collectReasoningText(delta.thinking_content, 0, true),
        ...collectReasoningText(delta.thinking, 0, true),
    ]
    const compactSegments = streamedSegments.filter((segment) => segment.trim().length > 0)
    if (compactSegments.length > 0) return compactSegments.join('')

    const structuredSegments = [
        ...collectReasoningText(delta.reasoning),
        ...collectReasoningText(delta.reasoning_details),
    ]
    if (structuredSegments.length === 0) return undefined
    return structuredSegments.join('\n').trim() || undefined
}

// ────────────────────────────────────────────────────────────────
// Message Building
// ────────────────────────────────────────────────────────────────

/** Build the messages array for the API request, with multipart support */
export async function buildMessages(
    config: AgentConfig,
    userMessage: string,
    refs: ResolvedRef[],
    attachments?: Attachment[],
    workspace?: Workspace,
    history?: AgentConversationHistory,
): Promise<ChatMessage[]> {
    const messages: ChatMessage[] = []
    const resolvedSystemPrompt = renderSystemPromptTemplate(config.systemPrompt)

    // System prompt
    if (resolvedSystemPrompt) {
        messages.push({ role: 'system', content: resolvedSystemPrompt })
    }

    // Insert conversation history (prior turns)
    if (history?.apiMode === 'chat-completions' && history.messages.length > 0) {
        for (const h of history.messages) {
            messages.push(h as ChatMessage)
        }
    }

    const content = workspace
        ? await buildChatUserContent(userMessage, refs, attachments, workspace)
        : userMessage
    messages.push({ role: 'user', content })

    return messages
}

export async function buildResponsesInput(
    config: AgentConfig,
    userMessage: string,
    refs: ResolvedRef[],
    attachments: Attachment[] | undefined,
    workspace: Workspace,
    history?: AgentConversationHistory,
): Promise<ResponseConversationItem[]> {
    const input = history?.apiMode === 'responses'
        ? normalizeResponsesReplayItems(history.items, {
            replayReasoningAsThinkingTokens: config.provider.responsesReplayReasoningAsThinkingTokens === true,
        })
        : []
    const userItem = await buildResponsesUserMessageItem(userMessage, refs, attachments, workspace)
    input.push(userItem)
    return input
}

function convertToolsToResponsesFormat(
    tools: Array<{ type: 'function'; function: { name: string; description: string; parameters: Record<string, unknown> } }>,
): ResponsesFunctionTool[] {
    return tools.map((tool) => ({
        type: 'function',
        name: tool.function.name,
        description: tool.function.description,
        parameters: tool.function.parameters,
    }))
}

function summarizeReasoningParts(summary: ResponsesReasoningOutput['summary'] | undefined): string[] | undefined {
    if (!summary || summary.length === 0) return undefined
    const parts = summary
        .map((entry) => typeof entry === 'string' ? entry : entry?.text)
        .filter((entry): entry is string => typeof entry === 'string' && entry.trim().length > 0)
    return parts.length > 0 ? parts : undefined
}

function parseResponseToolArgs(raw: string | undefined): Record<string, unknown> {
    if (!raw) return {}
    try {
        return JSON.parse(raw) as Record<string, unknown>
    } catch {
        return {}
    }
}

function asNonEmptyString(value: unknown): string | undefined {
    return typeof value === 'string' && value.trim().length > 0 ? value : undefined
}

function parseResponseFunctionCallItem(
    item: Record<string, unknown>,
): ResponseConversationFunctionCallItem | undefined {
    const callId = asNonEmptyString(item.call_id)
    const name = asNonEmptyString(item.name)
    if (!callId || !name) return undefined

    return {
        type: 'function_call',
        id: asNonEmptyString(item.id),
        call_id: callId,
        name,
        arguments: typeof item.arguments === 'string' ? item.arguments : '{}',
        status: asNonEmptyString(item.status),
    }
}

function getResponseMessageText(item: ResponsesOutputMessage | undefined): string {
    if (!item?.content) return ''
    return item.content
        .filter((part) => part.type === 'output_text' && typeof part.text === 'string')
        .map((part) => part.text ?? '')
        .join('')
}

function getResponseMessageContent(
    item: ResponsesOutputMessage | undefined,
): ResponseConversationMessageItem['content'] {
    if (!item?.content) return []
    return item.content.flatMap((part) => {
        if (part.type !== 'output_text' || typeof part.text !== 'string') return []
        return [{
            type: 'output_text' as const,
            text: part.text,
            ...(part.annotations ? { annotations: part.annotations } : {}),
        }]
    })
}

function toReplayResponseMessageItem(
    item: ResponsesOutputMessage | undefined,
): ResponseConversationMessageItem | undefined {
    if (!item || (item.role !== 'assistant' && item.role !== 'user')) return undefined
    const content = getResponseMessageContent(item)
    if (content.length === 0) return undefined
    return {
        type: 'message',
        ...(item.id ? { id: item.id } : {}),
        role: item.role,
        ...(item.status ? { status: item.status } : {}),
        ...(item.phase ? { phase: item.phase } : {}),
        content,
    }
}

function getReplayItemId(prefix: string, index: number): string {
    return `${prefix}_${index}`
}

function getReasoningSummaryText(item: ResponseConversationReasoningItem): string | undefined {
    const summaryText = item.summary
        ?.map((part) => part.text.trim())
        .filter((part) => part.length > 0)
        .join('\n\n')
    return summaryText || undefined
}

function buildThinkReplayText(thinkingText: string, assistantText: string): string {
    return assistantText
        ? `<thinking>${thinkingText}</thinking>\n${assistantText}`
        : `<thinking>${thinkingText}</thinking>`
}

function mergeReasoningIntoAssistantMessage(
    item: ResponseConversationMessageItem | undefined,
    thinkingText: string,
    index: number,
): ResponseConversationMessageItem {
    const outputParts = item?.content.filter((part): part is Extract<ResponseConversationMessageItem['content'][number], { type: 'output_text' }> => part.type === 'output_text') ?? []
    const assistantText = outputParts.map((part) => part.text).join('')
    const annotations = outputParts.flatMap((part) => part.annotations ?? [])

    return {
        type: 'message',
        id: item?.id ?? getReplayItemId('msg', index),
        role: 'assistant',
        status: item?.status ?? 'completed',
        ...(item?.phase ? { phase: item.phase } : {}),
        content: [{
            type: 'output_text',
            text: buildThinkReplayText(thinkingText, assistantText),
            ...(annotations.length > 0 ? { annotations } : {}),
        }],
    }
}

interface NormalizeResponsesReplayOptions {
    replayReasoningAsThinkingTokens?: boolean
}

export function normalizeResponsesReplayItems(
    items: ResponseConversationItem[],
    options: NormalizeResponsesReplayOptions = {},
): ResponseConversationItem[] {
    const normalized: ResponseConversationItem[] = []
    let pendingThinkingText: string[] = []

    const flushPendingThinking = (index: number) => {
        if (!options.replayReasoningAsThinkingTokens || pendingThinkingText.length === 0) return
        normalized.push(mergeReasoningIntoAssistantMessage(undefined, pendingThinkingText.join('\n\n'), index))
        pendingThinkingText = []
    }

    items.forEach((item, index) => {
        switch (item.type) {
            case 'message': {
                if (options.replayReasoningAsThinkingTokens && item.role === 'assistant' && pendingThinkingText.length > 0) {
                    normalized.push(mergeReasoningIntoAssistantMessage(item, pendingThinkingText.join('\n\n'), index))
                    pendingThinkingText = []
                    return
                }
                flushPendingThinking(index)
                if (item.content.length === 0) return
                normalized.push({
                    type: 'message',
                    ...(item.role === 'assistant'
                        ? {
                            id: item.id ?? getReplayItemId('msg', index),
                            status: item.status ?? 'completed',
                            ...(item.phase ? { phase: item.phase } : {}),
                        }
                        : {
                            ...(item.id ? { id: item.id } : {}),
                            ...(item.status ? { status: item.status } : {}),
                        }),
                    role: item.role,
                    content: item.content,
                } satisfies ResponseConversationMessageItem)
                return
            }
            case 'function_call':
                if (!asNonEmptyString(item.call_id)) return
                flushPendingThinking(index)
                normalized.push({
                    type: 'function_call',
                    id: item.id ?? getReplayItemId('fc', index),
                    call_id: item.call_id,
                    name: item.name,
                    arguments: item.arguments,
                    status: item.status ?? 'completed',
                })
                return
            case 'function_call_output':
                if (!asNonEmptyString(item.call_id)) return
                flushPendingThinking(index)
                normalized.push({
                    type: 'function_call_output',
                    id: item.id ?? getReplayItemId('fco', index),
                    call_id: item.call_id,
                    output: item.output,
                })
                return
            case 'reasoning': {
                if (!item.encrypted_content && (!item.summary || item.summary.length === 0)) {
                    return
                }
                if (options.replayReasoningAsThinkingTokens) {
                    const summaryText = getReasoningSummaryText(item)
                    if (summaryText) pendingThinkingText.push(summaryText)
                    return
                }
                normalized.push({
                    type: 'reasoning',
                    id: item.id ?? getReplayItemId('rs', index),
                    ...(item.encrypted_content ? { encrypted_content: item.encrypted_content } : {}),
                    ...(item.summary?.length ? { summary: item.summary } : {}),
                    status: item.status ?? 'completed',
                } satisfies ResponseConversationReasoningItem)
                return
            }
            default:
                flushPendingThinking(index)
                return
        }
    })
    flushPendingThinking(items.length)
    return normalized
}

// ────────────────────────────────────────────────────────────────
// Run Controller
// ────────────────────────────────────────────────────────────────

export function createOpenAiChatCompletionsRunController(
    config: AgentConfig,
    workspace: Workspace,
    dependencies: OpenAiChatCompletionsRunControllerDependencies = {},
): AgentRunController {
    let abortController: AbortController | null = null

    const baseUrl = config.provider.baseUrl || 'https://home.hoglundit.se/v1'
    const normalizedBaseUrl = baseUrl.replace(/\/+$/, '')
    const apiMode = getProviderApiMode(config.provider)
    const responseEndpointUrl = config.provider.useProxy === true
        ? buildResponsesProxyUrl(`${normalizedBaseUrl}/responses`)
        : `${normalizedBaseUrl}/responses`
    const chatCompletionsEndpointUrl = `${normalizedBaseUrl}/chat/completions`

    // Resolve fetch: use injected runtime fetch, falling back to the browser global.
    const runtimeFetch = dependencies.fetchImpl ?? fetch

    return {
        async *run(userMessage: string, refs: ResolvedRef[], attachments?: Attachment[], history?: AgentConversationHistory): AsyncIterableIterator<TranscriptEvent> {
            abortController = new AbortController()
            const signal = abortController.signal
            let includesImages = hasImageInputs(refs, attachments)

            const toolRegistry = await createAgentToolRegistry(config, workspace, runtimeFetch)
            const pricing = await getTokenPrice(config.model, baseUrl, config.provider.apiKey)
            let totalTokensIn = 0
            let totalTokensOut = 0
            const spendLimit = getSpendLimit(config)

            try {
                let iteration = 0
                const MAX_ITERATIONS = 12
                const messages = apiMode === 'chat-completions'
                    ? await buildMessages(config, userMessage, refs, attachments, workspace, history)
                    : []
                const responseInput = apiMode === 'responses'
                    ? await buildResponsesInput(config, userMessage, refs, attachments, workspace, history)
                    : []
                includesImages = includesImages
                    || hasImagesInChatMessages(messages)
                    || hasImagesInResponsesInput(responseInput)

                while (iteration < MAX_ITERATIONS) {
                    iteration++
                    yield { type: 'assistant_thinking', text: undefined, timestamp: Date.now() }

                    const headers = {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${config.provider.apiKey}`,
                        'HTTP-Referer': 'https://secureblob.hoglundit.se',
                        'X-Title': 'SecureBlob Agent',
                    }
                    const response = await runtimeFetch(
                        apiMode === 'responses' ? responseEndpointUrl : chatCompletionsEndpointUrl,
                        {
                            method: 'POST',
                            headers,
                            body: JSON.stringify(
                                apiMode === 'responses'
                                    ? {
                                        model: config.model,
                                        input: responseInput,
                                        instructions: renderSystemPromptTemplate(config.systemPrompt),
                                        stream: true,
                                        store: false,
                                        include: ['reasoning.encrypted_content'],
                                        max_output_tokens: 4096,
                                        tools: convertToolsToResponsesFormat(toolRegistry.definitions),
                                        tool_choice: 'auto',
                                        ...buildResponsesReasoningPayload(config.provider.reasoningEffort),
                                        ...(config.provider.plugins?.length ? { plugins: config.provider.plugins } : {}),
                                    }
                                    : {
                                        model: config.model,
                                        messages,
                                        stream: true,
                                        max_tokens: 4096,
                                        tools: toolRegistry.definitions,
                                        tool_choice: 'auto',
                                        ...buildReasoningPayload(baseUrl, config.provider.reasoningEffort),
                                        ...(config.provider.plugins?.length ? { plugins: config.provider.plugins } : {}),
                                        ...(config.provider.transforms ? { transforms: config.provider.transforms } : {}),
                                    },
                            ),
                            signal,
                        },
                    )
                    if (apiMode === 'responses' && isSecureBlobResponsesProxyUrl(responseEndpointUrl)) {
                        recordBrowserHttpErrorResponse(response, {
                            url: responseEndpointUrl,
                            method: 'POST',
                        })
                    }

                    if (!response.ok) {
                        const errorBody = await response.text()
                        let errorMsg = `Provider API error: ${response.status}`
                        try {
                            const errorJson = JSON.parse(errorBody)
                            errorMsg = errorJson?.error?.message || errorMsg
                        } catch { /* use status code error */ }
                        yield { type: 'error', message: errorMsg, retryable: response.status >= 500, timestamp: Date.now() }
                        return
                    }

                    if (!response.body) {
                        yield { type: 'error', message: 'No response body from provider', retryable: true, timestamp: Date.now() }
                        return
                    }

                    const reader = response.body.getReader()
                    if (apiMode === 'responses') {
                        let fullText = ''
                        const streamedToolCalls = new Map<string, ResponseConversationFunctionCallItem>()
                        const toolCallLookup = new Map<string, string>()
                        const emittedPendingToolCalls = new Set<string>()
                        const collectedReasoning = new Map<string, ResponseConversationReasoningItem>()
                        let finalMessageItem: ResponseConversationMessageItem | undefined
                        let latestUsage: ResponsesUsage | undefined

                        for await (const rawChunk of parseSSEStream(reader, signal)) {
                            if (signal.aborted) break
                            const event = rawChunk as unknown as ResponsesStreamEvent
                            const eventType = event.type ?? ''

                            if (eventType === 'response.output_item.added' || eventType === 'response.output_item.done') {
                                const item = (event.item ?? {}) as Record<string, unknown>
                                const itemType = typeof item.type === 'string' ? item.type : ''
                                if (itemType === 'function_call') {
                                    const next = parseResponseFunctionCallItem(item)
                                    if (!next) continue
                                    const callId = next.call_id
                                    streamedToolCalls.set(callId, next)
                                    if (next.id) toolCallLookup.set(next.id, callId)
                                    toolCallLookup.set(callId, callId)
                                    if (!emittedPendingToolCalls.has(callId)) {
                                        emittedPendingToolCalls.add(callId)
                                        yield {
                                            type: 'tool_call',
                                            id: callId,
                                            name: next.name,
                                            args: parseResponseToolArgs(next.arguments),
                                            status: 'pending',
                                            timestamp: Date.now(),
                                        }
                                    }
                                } else if (itemType === 'reasoning' && typeof item.id === 'string') {
                                    const reasoningId = item.id
                                    const reasoningItem: ResponseConversationReasoningItem = {
                                        type: 'reasoning',
                                        id: reasoningId,
                                        encrypted_content: typeof item.encrypted_content === 'string' ? item.encrypted_content : undefined,
                                        summary: summarizeReasoningParts(item.summary as ResponsesReasoningOutput['summary'])?.map((text) => ({ type: 'summary_text', text })),
                                        status: typeof item.status === 'string' ? item.status : undefined,
                                    }
                                    collectedReasoning.set(reasoningId, reasoningItem)
                                    if (eventType === 'response.output_item.done') {
                                        yield {
                                            type: 'assistant_reasoning_item',
                                            id: reasoningId,
                                            encryptedContent: reasoningItem.encrypted_content,
                                            summary: reasoningItem.summary?.map((part) => part.text),
                                            status: reasoningItem.status,
                                            timestamp: Date.now(),
                                        }
                                    }
                                } else if (itemType === 'message') {
                                    const messageItem = item as unknown as ResponsesOutputMessage
                                    const replayMessageItem = toReplayResponseMessageItem(messageItem)
                                    if (replayMessageItem) {
                                        finalMessageItem = replayMessageItem
                                    }
                                    const completedText = getResponseMessageText(messageItem)
                                    if (!fullText && completedText) {
                                        fullText = completedText
                                    }
                                }
                            }

                            if (eventType === 'response.function_call_arguments.delta' || eventType === 'response.function_call_arguments.done') {
                                const callId = typeof event.item_id === 'string'
                                    ? toolCallLookup.get(event.item_id) ?? event.item_id
                                    : (() => {
                                        const keys = [...streamedToolCalls.keys()]
                                        return keys[keys.length - 1]
                                    })()
                                if (callId) {
                                    const current = streamedToolCalls.get(callId)
                                    if (current) {
                                        const nextArgs = eventType === 'response.function_call_arguments.done'
                                            ? (event.arguments ?? current.arguments)
                                            : `${current.arguments}${event.delta ?? ''}`
                                        current.arguments = nextArgs
                                        streamedToolCalls.set(callId, current)
                                        yield {
                                            type: 'tool_call',
                                            id: callId,
                                            name: current.name,
                                            args: parseResponseToolArgs(nextArgs),
                                            status: 'running',
                                            timestamp: Date.now(),
                                        }
                                    }
                                }
                            }

                            if (eventType === 'response.output_text.delta' && event.delta) {
                                fullText += event.delta
                                yield {
                                    type: 'assistant_stream_delta',
                                    text: event.delta,
                                    timestamp: Date.now(),
                                }
                            }

                            if (
                                (eventType === 'response.reasoning.delta'
                                    || eventType === 'response.reasoning_text.delta'
                                    || eventType === 'response.reasoning_summary_text.delta')
                                && event.delta
                            ) {
                                yield {
                                    type: 'assistant_thinking',
                                    text: event.delta,
                                    timestamp: Date.now(),
                                }
                            }

                            if (eventType === 'response.completed') {
                                latestUsage = event.response?.usage
                                for (const item of event.response?.output ?? []) {
                                    const record = item as Record<string, unknown>
                                    const itemType = typeof record.type === 'string' ? record.type : ''
                                    if (itemType === 'function_call') {
                                        const call = parseResponseFunctionCallItem(record)
                                        if (!call) continue
                                        streamedToolCalls.set(call.call_id, call)
                                        if (call.id) toolCallLookup.set(call.id, call.call_id)
                                        toolCallLookup.set(call.call_id, call.call_id)
                                    } else if (itemType === 'reasoning' && typeof record.id === 'string') {
                                        const reasoningId = record.id
                                        const reasoningItem: ResponseConversationReasoningItem = {
                                            type: 'reasoning',
                                            id: reasoningId,
                                            encrypted_content: typeof record.encrypted_content === 'string' ? record.encrypted_content : undefined,
                                            summary: summarizeReasoningParts(record.summary as ResponsesReasoningOutput['summary'])?.map((text) => ({ type: 'summary_text', text })),
                                            status: typeof record.status === 'string' ? record.status : undefined,
                                        }
                                        collectedReasoning.set(reasoningId, reasoningItem)
                                    } else if (itemType === 'message') {
                                        const messageItem = record as unknown as ResponsesOutputMessage
                                        const replayMessageItem = toReplayResponseMessageItem(messageItem)
                                        if (replayMessageItem) {
                                            finalMessageItem = replayMessageItem
                                        }
                                        const messageText = getResponseMessageText(messageItem)
                                        if (!fullText && messageText) fullText = messageText
                                    }
                                }
                            }
                        }

                        if (latestUsage) {
                            totalTokensIn += latestUsage.input_tokens ?? 0
                            totalTokensOut += latestUsage.output_tokens ?? 0
                            const estimatedCostUsd = totalTokensIn * pricing.input + totalTokensOut * pricing.output
                            yield {
                                type: 'cost_update',
                                tokensIn: totalTokensIn,
                                tokensOut: totalTokensOut,
                                estimatedCostUsd,
                                timestamp: Date.now(),
                            }
                            if (spendLimit !== null && estimatedCostUsd >= spendLimit) {
                                yield { type: 'error', message: 'Spend limit reached', retryable: false, timestamp: Date.now() }
                                return
                            }
                        }

                        const responseToolCalls = [...streamedToolCalls.values()]
                        if (responseToolCalls.length === 0) {
                            yield {
                                type: 'assistant_message_final',
                                text: fullText,
                                ...(finalMessageItem?.id ? { id: finalMessageItem.id } : {}),
                                ...(finalMessageItem?.status ? { status: finalMessageItem.status } : {}),
                                ...(finalMessageItem?.phase ? { phase: finalMessageItem.phase } : {}),
                                ...(finalMessageItem?.content ? { content: finalMessageItem.content } : {}),
                                timestamp: Date.now(),
                            }
                            return
                        }

                        yield {
                            type: 'assistant_tool_batch',
                            items: responseToolCalls.map((toolCall) => ({
                                id: toolCall.call_id,
                                name: toolCall.name,
                                args: parseResponseToolArgs(toolCall.arguments),
                                ...(toolCall.id ? { responseId: toolCall.id } : {}),
                                ...(toolCall.status ? { status: toolCall.status } : {}),
                            })),
                            timestamp: Date.now(),
                        }

                        responseInput.push(...normalizeResponsesReplayItems([
                            ...collectedReasoning.values(),
                            ...responseToolCalls,
                        ], {
                            replayReasoningAsThinkingTokens: config.provider.responsesReplayReasoningAsThinkingTokens === true,
                        }))

                        for (const toolCall of responseToolCalls) {
                            const args = parseResponseToolArgs(toolCall.arguments)
                            const callId = toolCall.call_id
                            yield { type: 'tool_call', id: callId, name: toolCall.name, args, status: 'running', timestamp: Date.now() }

                            const { result, filesModified, isError } = await toolRegistry.execute(toolCall.name, args)
                            yield { type: 'tool_result', id: callId, name: toolCall.name, result, filesModified, timestamp: Date.now() }
                            yield { type: 'tool_call', id: callId, name: toolCall.name, args, status: isError ? 'error' : 'completed', timestamp: Date.now() }

                            responseInput.push({
                                type: 'function_call_output',
                                id: `fco_${callId}`,
                                call_id: callId,
                                output: result,
                            })
                        }

                        continue
                    }

                    let fullText = ''
                    const pendingToolCalls: ToolCallChunk[] = []

                    for await (const chunk of parseSSEStream(reader, signal)) {
                        if (signal.aborted) break

                        const choice = chunk.choices?.[0]
                        const delta = choice?.delta as {
                            content?: string
                            images?: Array<{ image_url?: { url?: string } }>
                            tool_calls?: ToolCallChunk[]
                            reasoning_content?: unknown
                            reasoning_text?: unknown
                            thinking?: unknown
                            thinking_content?: unknown
                            reasoning?: unknown
                            reasoning_details?: unknown
                        } | undefined

                        const thinkingText = extractThinkingDeltaText(delta)
                        if (thinkingText) {
                            yield {
                                type: 'assistant_thinking',
                                text: thinkingText,
                                timestamp: Date.now(),
                            }
                        }

                        if (delta?.content) {
                            fullText += delta.content
                            yield {
                                type: 'assistant_stream_delta',
                                text: delta.content,
                                timestamp: Date.now(),
                            }
                        }

                        if (delta?.images) {
                            for (const img of delta.images) {
                                const url = img?.image_url?.url
                                if (url) {
                                    yield {
                                        type: 'assistant_image',
                                        dataUrl: url,
                                        savedPath: '',
                                        timestamp: Date.now(),
                                    }
                                }
                            }
                        }

                        if (delta?.tool_calls) {
                            for (const tc of delta.tool_calls) {
                                if (tc.index !== undefined) {
                                    if (!pendingToolCalls[tc.index]) {
                                        pendingToolCalls[tc.index] = { id: tc.id, function: { name: '', arguments: '' } }
                                    }
                                    const target = pendingToolCalls[tc.index]
                                    if (tc.id) target.id = tc.id
                                    if (tc.function?.name) target.function!.name += tc.function.name
                                    if (tc.function?.arguments) target.function!.arguments += tc.function.arguments
                                }
                            }
                        }

                        if (chunk.usage) {
                            totalTokensIn += chunk.usage.prompt_tokens
                            totalTokensOut += chunk.usage.completion_tokens
                            const estimatedCostUsd = totalTokensIn * pricing.input + totalTokensOut * pricing.output
                            yield {
                                type: 'cost_update',
                                tokensIn: totalTokensIn,
                                tokensOut: totalTokensOut,
                                estimatedCostUsd,
                                timestamp: Date.now(),
                            }
                            if (spendLimit !== null && estimatedCostUsd >= spendLimit) {
                                yield { type: 'error', message: 'Spend limit reached', retryable: false, timestamp: Date.now() }
                                return
                            }
                        }
                    }

                    const assistantMsg: ChatMessage = { role: 'assistant', content: fullText || null }
                    if (pendingToolCalls.length > 0) assistantMsg.tool_calls = pendingToolCalls
                    messages.push(assistantMsg)

                    if (pendingToolCalls.length === 0) {
                        yield { type: 'assistant_message_final', text: fullText, timestamp: Date.now() }
                        return
                    }

                    const normalizedToolCalls = pendingToolCalls
                        .filter((tc): tc is ToolCallChunk & { function: { name: string; arguments: string } } => !!tc?.function?.name)
                        .map((tc) => ({
                            id: tc.id ?? generateId(),
                            name: tc.function.name ?? 'unknown',
                            rawArguments: tc.function.arguments,
                        }))

                    yield {
                        type: 'assistant_tool_batch',
                        items: normalizedToolCalls.map((toolCall) => ({
                            id: toolCall.id,
                            name: toolCall.name,
                            args: parseResponseToolArgs(toolCall.rawArguments),
                        })),
                        timestamp: Date.now(),
                    }

                    for (const toolCall of normalizedToolCalls) {
                        const callId = toolCall.id
                        const name = toolCall.name
                        const args = parseResponseToolArgs(toolCall.rawArguments)

                        yield { type: 'tool_call', id: callId, name, args, status: 'running', timestamp: Date.now() }

                        const { result, filesModified, isError } = await toolRegistry.execute(name, args)

                        yield { type: 'tool_result', id: callId, name, result, filesModified, timestamp: Date.now() }
                        yield { type: 'tool_call', id: callId, name, args, status: isError ? 'error' : 'completed', timestamp: Date.now() }

                        messages.push({
                            role: 'tool',
                            tool_call_id: callId,
                            name,
                            content: result,
                        })
                    }
                }

                yield { type: 'error', message: 'Maximum tool calling iterations reached', retryable: false, timestamp: Date.now() }

            } catch (err) {
                if (err instanceof DOMException && err.name === 'AbortError') {
                    return
                }
                yield {
                    type: 'error',
                    message: formatProviderTransportError(err, includesImages),
                    retryable: true,
                    timestamp: Date.now(),
                }
            }
        },

        abort() {
            abortController?.abort()
        },
    }
}
